import React, {
  useState,
  ChangeEvent,
  KeyboardEvent,
  Fragment,
  useEffect,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormHelperText,
  Chip,
  Stepper,
  Step,
  StepLabel,
} from '@material-ui/core';
import {AddCircleOutline, DeleteOutline} from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ExcelJS from 'exceljs';
import {saveAs} from 'file-saver';
import {emailRegex, splitEmails} from '../InviteCodePanel/utils/email';
import {apiFarmerExists} from 'constants/Api';

/* ---------- types ---------- */
export interface FarmerDraft {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address?: string;
  company?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (existingFarmerEmails: string[], newFarmers: FarmerDraft[]) => void;
  isSubmitting: boolean;
}

const farmerLookup = async (emails: string[]) => {
  const existingFarmers: FarmerDraft[] = [];
  const unknownEmails: string[] = [];
  for (const email of emails) {
    await apiFarmerExists(email)
      .then((farmer) => {
        if (farmer)
          existingFarmers.push({
            id: farmer.id,
            email: farmer.email,
            firstName: farmer.name,
            lastName: farmer.surname,
            phone: farmer.phone,
            address: farmer.address,
            company: farmer.farm?.name,
          });
        else unknownEmails.push(email);
      })
      .catch((e: Error) => {
        alert(`Error checking farmer: ${email}. Please try again later.`);
        console.error('Error checking farmer:', email, e);
      });
  }
  return {existing: existingFarmers, unknown: unknownEmails};
};

/* ---------- helpers ---------- */
const blankDraft = (email = ''): FarmerDraft => ({
  firstName: '',
  lastName: '',
  email,
  phone: '',
  address: '',
  company: '',
});

/* ==================================================================== */
const InviteFarmersDialog: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  type StepKey = 'EMAILS' | 'DETAILS';
  const [step, setStep] = useState<StepKey>('EMAILS');

  /* step-1 state */
  const [emailInput, setEmailInput] = useState('');
  const [emailChips, setEmailChips] = useState<string[]>([]);
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [uploadErr, setUploadErr] = useState('');

  /* step-2 state */
  const [existing, setExisting] = useState<FarmerDraft[]>([]);
  const [drafts, setDrafts] = useState<FarmerDraft[]>([]);
  const [loadingLookup, setLoadingLookup] = useState(false);

  // --- NEW: Store uploaded farmer details from template for prepopulation ---
  const [uploadedFarmerDetails, setUploadedFarmerDetails] = useState<
    FarmerDraft[]
  >([]);

  /* ---------- chip helpers ---------- */
  const normaliseEmails = (arr: string[]) =>
    Array.from(new Set(arr.map((e) => e.trim().toLowerCase()).filter(Boolean)));

  const pushEmails = (arr: string[]) => {
    const uniq = normaliseEmails([...emailChips, ...arr]);
    setEmailChips(uniq);
    setInvalidEmails(uniq.filter((e) => !emailRegex.test(e)));
  };

  /* ---------- Autocomplete events ---------- */
  const handleEmailInput = (_: any, value: string, reason: string) => {
    if (reason === 'reset') return;
    if (/[,\s]$/.test(value)) {
      pushEmails(splitEmails(value));
      setEmailInput('');
    } else setEmailInput(value);
  };

  const handleChipChange = (_: any, value: (string | string[])[]) => {
    /* value may contain nested arrays => flatten first */
    const flat = ([] as string[]).concat(...value);
    const uniq = normaliseEmails(flat);
    setEmailChips(uniq);
    setInvalidEmails(uniq.filter((e) => !emailRegex.test(e)));
  };

  /* ✦ FIX #1 — clear ghost text & single chip */
  const handleEmailKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (emailInput.trim()) {
        pushEmails(splitEmails(emailInput));
        setEmailInput('');
      }
    }
  };

  /* ---------- template download ---------- */
  const downloadTemplate = async (withDetails: boolean) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Farmers');
    ws.columns = [
      {header: 'Email', key: 'email', width: 25},
      {header: 'First Name', key: 'fn', width: 15},
      {header: 'Last Name', key: 'ln', width: 15},
      {header: 'Phone', key: 'ph', width: 15},
      {header: 'Address', key: 'addr', width: 20},
      {header: 'Company', key: 'co', width: 20},
    ];

    if (withDetails) {
      drafts.forEach((d) => ws.addRow({email: d.email}));
      if (drafts.length === 0) ws.addRow({email: ''});
    } else {
      ws.addRow({
        email: 'farmer1@example.com',
        fn: 'John',
        ln: 'Doe',
      });
      ws.addRow({email: 'farmer2@example.com'});
      ws.addRow({email: 'test@example.com'});
    }

    const buf = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      withDetails
        ? 'farmer_details_template.xlsx'
        : 'farmer_email_template.xlsx',
    );
  };

  /* ---------- uploads ---------- */
  const parseUpload = async (
    e: ChangeEvent<HTMLInputElement>,
    asDetails = false,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploadErr('');
    try {
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(await file.arrayBuffer());
      const ws = wb.worksheets[0];

      if (asDetails) {
        const newDrafts: FarmerDraft[] = [];
        ws.eachRow((row, idx) => {
          if (idx === 1) return;
          const [email, fn, ln, ph, addr, co] = [
            row.getCell(1).text,
            row.getCell(2).text,
            row.getCell(3).text,
            row.getCell(4).text,
            row.getCell(5).text,
            row.getCell(6).text,
          ].map((t) => t.trim());
          if (email) {
            newDrafts.push({
              email,
              firstName: fn,
              lastName: ln,
              phone: ph,
              address: addr,
              company: co,
            });
          }
        });
        setDrafts((d) => [...d, ...newDrafts]);
      } else {
        const emails: string[] = [];
        const details: FarmerDraft[] = [];
        ws.eachRow((row, idx) => {
          if (idx === 1) return;
          const email = row.getCell(1).text.trim();
          emails.push(email);
          // Try to extract details if present
          const fn = row.getCell(2)?.text?.trim?.() || '';
          const ln = row.getCell(3)?.text?.trim?.() || '';
          const ph = row.getCell(4)?.text?.trim?.() || '';
          const addr = row.getCell(5)?.text?.trim?.() || '';
          const co = row.getCell(6)?.text?.trim?.() || '';
          if (email) {
            details.push({
              email,
              firstName: fn,
              lastName: ln,
              phone: ph,
              address: addr,
              company: co,
            });
          }
        });
        pushEmails(emails);
        setUploadedFarmerDetails(details); // Store for prepopulation
      }
    } catch (err) {
      console.error(err);
      setUploadErr('Could not read file – please verify the template.');
    }
  };

  /* ---------- step change ---------- */
  const handleContinue = async () => {
    setLoadingLookup(true);
    const {existing: ex, unknown} = await farmerLookup(emailChips);

    // --- NEW: Prepopulate drafts with uploaded details if available ---
    let unknownDrafts: FarmerDraft[];
    if (uploadedFarmerDetails.length > 0) {
      // For each unknown email, try to find a matching uploaded detail
      unknownDrafts = unknown.map((email) => {
        const found = uploadedFarmerDetails.find(
          (d) => d.email.toLowerCase() === email.toLowerCase(),
        );
        return found ? {...found} : blankDraft(email);
      });
    } else {
      unknownDrafts = unknown.map(blankDraft);
    }

    setExisting(ex);
    setDrafts(unknownDrafts);
    setLoadingLookup(false);
    setStep('DETAILS');
  };

  /* ---------- grid helpers ---------- */
  const addDraftRow = () => setDrafts((d) => [...d, blankDraft()]);
  const deleteDraftRow = (i: number) =>
    setDrafts((d) => d.filter((_, k) => k !== i));
  const updateDraft = (i: number, field: keyof FarmerDraft, val: string) => {
    setDrafts((d) => {
      const clone = [...d];
      clone[i] = {...clone[i], [field]: val};
      return clone;
    });
  };

  /* ---------- submit ---------- */
  const handleInvite = () => {
    const okDrafts = drafts.filter((d) => emailRegex.test(d.email));
    onSubmit(
      existing.map((e) => e.email),
      okDrafts,
    );
  };

  useEffect(() => {
    setStep('EMAILS');
    setEmailChips([]);
    setDrafts([]);
    setExisting([]);
  }, [open]);

  /* ---------- derived counts ---------- */
  const validEmailCount = emailChips.filter((e) => emailRegex.test(e)).length;
  const validDraftCount = drafts.filter((d) => emailRegex.test(d.email)).length;

  /* ================================================================== */
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Invite farmers</DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={step === 'EMAILS' ? 0 : 1}>
          <Step>
            <StepLabel>E-mail addresses</StepLabel>
          </Step>
          <Step>
            <StepLabel>Details for new farmers</StepLabel>
          </Step>
        </Stepper>

        {/* ─── STEP 1 ─── */}
        {step === 'EMAILS' && (
          <>
            <Box
              display="flex"
              justifyContent="flex-end"
              mb={1}
              style={{gap: 8}}
            >
              <input
                id="upload-emails"
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => parseUpload(e, false)}
              />
              <label htmlFor="upload-emails">
                <Button color="primary" component="span">
                  Upload file
                </Button>
              </label>
              <Button color="primary" onClick={() => downloadTemplate(false)}>
                Download template
              </Button>
            </Box>

            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={emailChips}
              onChange={handleChipChange}
              inputValue={emailInput}
              onInputChange={handleEmailInput}
              /* 🔽——— FIX: ensure each tag is a string ———🔽 */
              renderTags={(value, getTagProps) =>
                value.map((v, i) => {
                  const email = Array.isArray(v) ? v.join(',') : v; // ← coerce
                  return (
                    <Chip
                      {...getTagProps({index: i})}
                      key={email}
                      label={email}
                      color={emailRegex.test(email) ? 'default' : 'secondary'}
                    />
                  );
                })
              }
              /* ───────────────────────────────────────────── */
              renderInput={(params) => (
                <TextField
                  {...params}
                  onKeyDown={handleEmailKey}
                  variant="outlined"
                  label="Paste or type e-mails"
                />
              )}
            />

            {invalidEmails.length > 0 && (
              <FormHelperText error>
                Invalid: {invalidEmails.join(', ')}
              </FormHelperText>
            )}

            {loadingLookup && (
              <Box mt={1} display="flex" alignItems="center" style={{gap: 8}}>
                <CircularProgress size={16} /> <span>Checking…</span>
              </Box>
            )}
          </>
        )}

        {/* ─── STEP 2 ─── */}
        {step === 'DETAILS' && (
          <>
            {existing.length > 0 && (
              <Box mb={2}>
                <strong>Existing farmers ({existing.length})</strong>
                <Box mt={1} display="flex" flexWrap="wrap" style={{gap: 8}}>
                  {existing.map((e) => (
                    <Chip
                      key={e.email}
                      label={`${e.email} ✓`}
                      color="primary"
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box
              display="flex"
              justifyContent="flex-end"
              mb={1}
              style={{gap: 8}}
            >
              <input
                id="upload-details"
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => parseUpload(e, true)}
              />
              <label htmlFor="upload-details">
                <Button color="primary" component="span">
                  Upload details
                </Button>
              </label>
              <Button color="primary" onClick={() => downloadTemplate(true)}>
                Download details template
              </Button>
            </Box>

            {uploadErr && (
              <FormHelperText error style={{marginBottom: 8}}>
                {uploadErr}
              </FormHelperText>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    'First Name',
                    'Last Name',
                    'Email',
                    'Phone',
                    'Address',
                    'Company',
                    '',
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {drafts.map((d, i) => (
                  <Fragment key={i}>
                    <TableRow>
                      {(
                        [
                          'firstName',
                          'lastName',
                          'email',
                          'phone',
                          'address',
                          'company',
                        ] as (keyof FarmerDraft)[]
                      ).map((field) => (
                        <TableCell key={field}>
                          <TextField
                            variant="outlined"
                            size="small"
                            value={d[field]}
                            onChange={(e) =>
                              updateDraft(i, field, e.target.value)
                            }
                            error={
                              field === 'email' &&
                              !!d.email &&
                              !emailRegex.test(d.email)
                            }
                          />
                        </TableCell>
                      ))}
                      <TableCell style={{width: 46}}>
                        <IconButton
                          size="small"
                          onClick={() => deleteDraftRow(i)}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>

            <Box mt={1}>
              <Button
                startIcon={<AddCircleOutline />}
                color="primary"
                onClick={addDraftRow}
              >
                Add row
              </Button>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'EMAILS' ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              disabled={validEmailCount === 0}
              onClick={handleContinue}
            >
              Continue
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep('EMAILS')}>Back</Button>
            <Tooltip title="Only rows with a valid e-mail will be sent">
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    (existing.length === 0 && validDraftCount === 0) ||
                    isSubmitting
                  }
                  onClick={handleInvite}
                >
                  {isSubmitting ? (
                    <CircularProgress color="primary" size={24} thickness={6} />
                  ) : (
                    'Invite'
                  )}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InviteFarmersDialog;
